import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, FileText, Clock, ShieldCheck, AlertTriangle, Ban, XCircle, Scale } from 'lucide-react';
import { SEO } from '../components/SEO';

function TermsPage() {
  const navigate = useNavigate();

  const sections = [
    {
      num: '1',
      title: 'Booking and Payments',
      icon: <Clock className="h-4 w-4 text-[#16A34A]" />,
      items: [
        { label: 'Confirmation', text: 'A booking is only considered confirmed once the full payment (or the required advance) is received through our website or authorized payment channels.' },
        { label: 'Accuracy', text: 'Users are responsible for providing correct contact details. Malda Turf is not responsible for missed communication due to incorrect information.' },
        { label: 'Pricing', text: 'We reserve the right to change slot pricing at any time. However, confirmed bookings will be honored at the price paid at the time of booking.' },
      ]
    },
    {
      num: '2',
      title: 'Use of Facility',
      icon: <ShieldCheck className="h-4 w-4 text-[#16A34A]" />,
      items: [
        { label: 'Reporting Time', text: 'Players should arrive at least 10-15 minutes before their scheduled slot. Playing time will not be extended if a group arrives late.' },
        { label: 'Footwear', text: 'Only turf shoes, flat-soled sports shoes, or sneakers are allowed. Metal spikes or studs are strictly prohibited as they damage the turf surface.' },
        { label: 'Conduct', text: 'Users must maintain decorum. Abusive language, fighting, or any behavior that disturbs other users or neighbors will result in immediate eviction without a refund.' },
      ]
    },
    {
      num: '3',
      title: 'Safety and Liability',
      icon: <AlertTriangle className="h-4 w-4 text-[#16A34A]" />,
      items: [
        { label: 'Risk of Injury', text: 'Sports activities involve inherent risks. By booking, you acknowledge that Malda Turf is not liable for any injuries, medical conditions, or accidents sustained by players or spectators on the premises.' },
        { label: 'Medical Fitness', text: 'Players are advised to ensure they are physically fit to play.' },
        { label: 'Personal Belongings', text: 'Malda Turf is not responsible for the loss, theft, or damage of personal items (phones, bags, vehicles, etc.) brought to the venue.' },
      ]
    },
    {
      num: '4',
      title: 'Damage to Property',
      icon: <AlertTriangle className="h-4 w-4 text-[#16A34A]" />,
      text: 'Any damage caused to the turf, netting, fencing, lighting, or equipment due to negligence or intentional misuse will be the financial responsibility of the person who made the booking.'
    },
    {
      num: '5',
      title: 'Prohibited Activities',
      icon: <Ban className="h-4 w-4 text-[#16A34A]" />,
      intro: 'To maintain a family-friendly environment, the following are strictly prohibited inside the turf area:',
      bullets: [
        'Smoking or consumption of alcohol/drugs.',
        'Chewing gum, tobacco, or spitting.',
        'Bringing pets onto the playing surface.',
        'Carrying sharp objects or glassware.',
      ]
    },
    {
      num: '6',
      title: 'Termination of Booking',
      icon: <XCircle className="h-4 w-4 text-[#16A34A]" />,
      intro: 'Malda Turf reserves the right to cancel a booking or ask a group to leave if:',
      bullets: [
        'They violate any of the terms mentioned above.',
        'The number of players exceeds the maximum capacity allowed for the venue.',
      ]
    },
    {
      num: '7',
      title: 'Governing Law',
      icon: <Scale className="h-4 w-4 text-[#16A34A]" />,
      text: 'These terms are governed by the laws of India. Any disputes arising out of the use of the facility shall be subject to the exclusive jurisdiction of the courts in Malda, West Bengal.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Terms and Conditions"
        description="Read the Terms and Conditions for booking and using Malda Turf cricket and football grounds."
        path="/terms"
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[#16A34A] flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50px' }}>
              <Trophy className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-base font-bold tracking-tight font-heading">Malda Turf</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white transition-colors font-body">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#16A34A]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading">Terms and Conditions</h1>
                <p className="text-[11px] text-neutral-500 font-body mt-0.5">Last updated: April 2026</p>
              </div>
            </div>
            <p className="text-sm text-neutral-400 font-body leading-relaxed">
              Welcome to Malda Turf. These Terms and Conditions govern your use of our website (<a href="https://maldaturf.in" className="text-[#16A34A] hover:underline">maldaturf.in</a>) and the rental of our sports facilities. By booking a slot, you agree to comply with and be bound by the following terms.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.num} className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid={`terms-section-${section.num}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">{section.num}</span>
                  <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">{section.title}</h2>
                </div>
                <div className="pl-10">
                  {/* Sub-items with labels */}
                  {section.items && (
                    <div className="space-y-4">
                      {section.items.map((item, i) => (
                        <div key={i} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4">
                          <div className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1.5 font-heading">{item.label}</div>
                          <p className="text-sm text-neutral-400 font-body leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Plain text */}
                  {section.text && (
                    <p className="text-sm text-neutral-400 font-body leading-relaxed">{section.text}</p>
                  )}
                  {/* Intro + bullets */}
                  {section.intro && (
                    <>
                      <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">{section.intro}</p>
                      <ul className="space-y-2.5">
                        {section.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                            <span className="text-sm text-neutral-400 font-body leading-relaxed">{b}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-[11px] text-neutral-600 font-body">
              By booking a slot on Malda Turf, you acknowledge and agree to the terms outlined above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsPage;
